import axios from 'axios'
import { MessageBox, Message } from 'element-ui'
import store from '@/store'
import { getToken } from '@/utils/auth'
import { debounce, isPlainObject } from 'lodash-es'

const reloginCheck = debounce(config => {
  if (config.skipConfirm) {
    return store.dispatch('user/resetToken').then(() => {
      location.reload()
    })
  }
  MessageBox.confirm('您已被登出，您可以取消以停留在当前页面，或者重新登录', '确认登出', {
    confirmButtonText: '重新登录',
    cancelButtonText: '取消',
    showCancelButton: false,
    showClose: false,
    closeOnPressEscape: false,
    closeOnClickModal: false,
    type: 'warning',
  }).then(() => {
    store.dispatch('user/resetToken').then(() => {
      location.reload()
    })
  })
}, 300)

const SUCEESS_CODES = [0]
const RELOGIN_CODES = [401]

/**
 * 反转映射关系
 * @param {Object} mapping - 原始映射
 * @returns {Object} - 反转后的映射
 */
const invertMapping = mapping => {
  const inverted = {}
  for (const [from, to] of Object.entries(mapping)) {
    inverted[to] = from
  }
  return inverted
}

// 字符映射关系：request时A->B，response时B->A
const keyMapping = {
  // play: 'bet', // 示例：request中'_'转为'-'，response中'-'转为'_'
  // 可添加更多映射关系
}
const invertedKeyMapping = invertMapping(keyMapping)

/**
 * 递归处理对象中的所有键，根据映射转换字段名
 * @param {any} data - 要处理的数据
 * @param {Object} mapping - 字段名映射关系
 * @param {WeakSet} [processed] - 用于跟踪已处理对象，防止循环引用
 * @returns {any} - 处理后的数据
 */
const processKeys = (data, mapping, processed = new WeakSet()) => {
  if (data === null || typeof data !== 'object') {
    return data
  }

  // 检查是否已处理过该对象，防止循环引用导致无限递归
  if (processed.has(data)) {
    return data
  }
  processed.add(data)

  if (Array.isArray(data)) {
    return data.map(item => processKeys(item, mapping, processed))
  }

  const result = {}
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      // 替换键中包含的特定字段
      let newKey = key
      for (const [from, to] of Object.entries(mapping)) {
        if (newKey.includes(from)) {
          newKey = newKey.replaceAll(from, to)
        }
      }
      // 递归处理值
      result[newKey] = processKeys(data[key], mapping, processed)
    }
  }
  return result
}

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 2 * 60000, // request timeout
  headers: {
    'Accept-Language': 'PT-BR',
  },
})

export function setBaseURL(baseURL) {
  service.defaults.baseURL = baseURL
}

export function getBaseURL() {
  return service.defaults.baseURL
}

// request interceptor
service.interceptors.request.use(
  config => {
    // do something before request is sent

    // 根据mock配置动态修改baseURL
    if (config.mock && process.env.VUE_APP_BASE_API_MOCK) {
      config.baseURL = process.env.VUE_APP_BASE_API_MOCK
    }

    if (store.getters.token) {
      // let each request carry token
      // ['X-Token'] is a custom headers key
      // please modify it according to the actual situation
      config.headers['Authorization'] = `Bearer ${getToken()}`
    }

    // 处理JSON请求体中的key转换
    if (isPlainObject(config.data)) {
      config.data = processKeys(config.data, keyMapping)
    }

    // 增加loading计数
    store.commit('app/INCREMENT_LOADING')

    return config
  },
  error => {
    // do something with request error
    console.log(error) // for debug
    // 请求失败时也需要减少loading计数
    store.commit('app/DECREMENT_LOADING')
    return Promise.reject(error)
  }
)

// response interceptor
service.interceptors.response.use(
  /**
   * If you want to get http information such as headers or status
   * Please return  response => response
   */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code
   */
  response => {
    let res = response.data

    // 处理JSON响应数据中的key转换
    if (isPlainObject(res)) {
      res = processKeys(res, invertedKeyMapping)
    }

    // 减少loading计数
    store.commit('app/DECREMENT_LOADING')

    // if the custom code is not 20000, it is judged as an error.
    if (!SUCEESS_CODES.includes(res.code)) {
      console.error('Response Error', res)
      Message({
        message: res.msg || '网络异常',
        type: 'error',
        duration: 5 * 1000,
      })

      // 50008: Illegal token; 50012: Other clients logged in; 50014: Token expired;
      if (RELOGIN_CODES.includes(res.code)) {
        // to re-login
        reloginCheck(response.config)
      }
      return Promise.reject(new Error(res.msg || '网络异常'))
    } else {
      return res
    }
  },
  error => {
    const { response } = error
    console.log('error keys', Object.keys(error), error.config)
    console.error(
      'HTTP Error',
      response?.data?.msg,
      error.message,
      error.code === 'ECONNABORTED' ? '请求超时或网络异常' : '网络异常'
    )
    Message({
      message:
        response?.data?.msg || error.message || (error.code === 'ECONNABORTED' ? '请求超时或网络异常' : '网络异常'),
      type: 'error',
      duration: 5 * 1000,
    })
    // 减少loading计数
    store.commit('app/DECREMENT_LOADING')
    if (RELOGIN_CODES.includes(response?.data?.code) || RELOGIN_CODES.includes(response?.status)) {
      // to re-login
      reloginCheck(error.config)
    }
    return Promise.reject(error)
  }
)

export default service
